import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, type LookupItem, type Movement, type Warehouse } from '../api/client'
import { Button, EmptyState, Input, Notice, Select, Spinner } from '../ui/controls'
import ProductPicker from '../ui/ProductPicker'

/**
 * Экран быстрого списания продажи (п. 4.14).
 *
 * Работа идёт с клавиатуры: поиск позиции, количество, подтверждение.
 * После списания фокус возвращается в поле поиска, поля очищаются
 * (А-1303). Склад выбирается один раз и запоминается (А-1307).
 */

const WAREHOUSE_KEY = 'sale.warehouse'

export default function SalePage() {
  const [warehouses, setWarehouses] = useState<Warehouse[] | null>(null)
  const [warehouseId, setWarehouseId] = useState<number | null>(null)
  const [picked, setPicked] = useState<LookupItem | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [recent, setRecent] = useState<Movement[]>([])
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [pending, setPending] = useState(false)
  const [undo, setUndo] = useState<{ id: number; until: number } | null>(null)
  const [now, setNow] = useState(Date.now())

  const qtyRef = useRef<HTMLInputElement>(null)
  const pickerKey = useRef(0)

  useEffect(() => {
    void api.warehouses().then((list) => {
      const active = list.filter((w) => w.is_active)
      setWarehouses(active)
      // Склад запоминается между сеансами: кладовщик работает на одном.
      const saved = Number(localStorage.getItem(WAREHOUSE_KEY))
      const initial = active.find((w) => w.id === saved)?.id ?? active[0]?.id ?? null
      setWarehouseId(initial)
    })
  }, [])

  const loadRecent = useCallback(async (wid: number) => {
    setRecent(await api.recentSales(wid))
  }, [])

  useEffect(() => {
    if (warehouseId === null) return
    localStorage.setItem(WAREHOUSE_KEY, String(warehouseId))
    void loadRecent(warehouseId)
  }, [warehouseId, loadRecent])

  // Обратный отсчёт срока отмены (А-1305).
  useEffect(() => {
    if (!undo) return
    const t = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(t)
  }, [undo])

  const undoLeft = undo ? Math.max(0, Math.ceil((undo.until - now) / 1000)) : 0
  useEffect(() => {
    if (undo && undoLeft === 0) setUndo(null)
  }, [undo, undoLeft])

  async function sell() {
    if (!picked || warehouseId === null) return
    const qty = Number(quantity) || 1
    setPending(true)
    setNotice(null)
    try {
      const res = await api.sell({
        product_id: picked.product_id,
        warehouse_id: warehouseId,
        quantity: qty,
      })
      setUndo({ id: res.movement_id, until: Date.now() + res.undo_seconds * 1000 })
      setNotice({ kind: 'success', text: `Списано: ${picked.name} — ${qty} шт.` })
      setPicked(null)
      setQuantity('1')
      pickerKey.current += 1
      await loadRecent(warehouseId)
    } catch (e) {
      if (e instanceof ApiError) {
        const d = e.details as { available?: number }
        setNotice({
          kind: 'error',
          text:
            d.available !== undefined
              ? `${e.message}. Доступно на складе: ${d.available}.`
              : e.message,
        })
      } else {
        setNotice({ kind: 'error', text: 'Не удалось списать' })
      }
    } finally {
      setPending(false)
    }
  }

  if (warehouses === null) return <Spinner label="Загрузка…" />

  if (warehouses.length === 0) {
    return (
      <EmptyState
        title="Нет активных складов"
        hint="Заведите склад в разделе «Склады» — без него списание невозможно."
      />
    )
  }

  return (
    <section className="max-w-3xl">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Продажа</h2>
        <p className="mt-0.5 text-sm text-ink-500">
          Быстрое списание. Найдите позицию, укажите количество и подтвердите — всё с клавиатуры.
        </p>
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {undo && undoLeft > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-md border border-warning-500/40 bg-warning-100 dark:bg-warning-700/25 px-3 py-2 text-sm text-warning-700 dark:text-warning-100">
          <span className="flex-1">Списание можно отменить ещё {undoLeft} с</span>
          <Button
            size="sm"
            onClick={async () => {
              try {
                await api.undoSale(undo.id)
                setUndo(null)
                setNotice({ kind: 'success', text: 'Отменено, товар возвращён на склад' })
                if (warehouseId !== null) await loadRecent(warehouseId)
              } catch (e) {
                setNotice({
                  kind: 'error',
                  text: e instanceof ApiError ? e.message : 'Не удалось отменить',
                })
              }
            }}
          >
            Отменить
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-800">
        <label className="mb-4 block">
          <span className="mb-1.5 block text-sm font-medium text-ink-700 dark:text-ink-300">
            Склад
          </span>
          <Select value={warehouseId ?? ''} onChange={(e) => setWarehouseId(Number(e.target.value))}
            className="rounded-md border border-ink-300 bg-white px-3 py-2 text-sm dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
          >
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </label>

        {picked ? (
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md bg-ink-50 p-3 dark:bg-ink-900">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-ink-900 dark:text-ink-50">{picked.name}</div>
              <div className="font-mono text-xs text-ink-500">
                {picked.sku} · остаток {picked.stock}
              </div>
            </div>
            <label className="block">
              <span className="mb-1 block text-xs text-ink-500">Количество</span>
              <Input
                ref={qtyRef}
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void sell()
                  if (e.key === 'Escape') setPicked(null)
                }}
                autoFocus
                className="w-24"
              />
            </label>
            <Button variant="primary" onClick={sell} disabled={pending}>
              {pending ? 'Списание…' : 'Списать'}
            </Button>
            <Button onClick={() => setPicked(null)} disabled={pending}>
              Отмена
            </Button>
          </div>
        ) : (
          <ProductPicker
            key={pickerKey.current}
            warehouseId={warehouseId ?? undefined}
            autoFocus
            placeholder="Артикул, штрихкод или название — Enter выбирает"
            onPick={(item) => {
              setPicked(item)
              // Фокус переходит в поле количества (А-902).
              setTimeout(() => qtyRef.current?.select(), 0)
            }}
          />
        )}
      </div>

      <div className="mt-6">
        <h3 className="mb-2 text-sm font-semibold text-ink-900 dark:text-ink-50">
          Последние операции смены
        </h3>
        {recent.length === 0 ? (
          <p className="text-sm text-ink-500">Операций пока не было.</p>
        ) : (
          <ul className="divide-y divide-ink-200 rounded-lg border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
            {recent.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-32 shrink-0 tabular-nums text-xs text-ink-400">
                  {m.created_at.slice(11)}
                </span>
                <span className="min-w-0 flex-1 truncate text-ink-800 dark:text-ink-200">
                  {m.product_name}
                </span>
                <span className="shrink-0 tabular-nums text-ink-600 dark:text-ink-400">
                  {m.delta}
                </span>
                {m.reason_name.includes('отменена') && (
                  <span className="shrink-0 text-xs text-ink-400">отменена</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
